import { useState, useRef } from "react";

interface TooltipProps {
  content: string;
  children: React.ReactNode;
  position?: "top" | "bottom" | "left" | "right";
}

export function Tooltip({ content, children, position = "top" }: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const tooltipId = useRef(
    `tooltip-${Math.random().toString(36).substr(2, 9)}`
  );

  const showTooltip = () => setIsVisible(true);
  const hideTooltip = () => setIsVisible(false);

  const positionClasses = {
    top: "bottom-full left-1/2 -translate-x-[40%] mb-2 w-[280px] max-w-[calc(100vw-2rem)] md:-translate-x-1/2 md:w-auto md:max-w-xs",
    bottom:
      "top-full left-1/2 -translate-x-[40%] mt-2 w-[280px] max-w-[calc(100vw-2rem)] md:-translate-x-1/2 md:w-auto md:max-w-xs",
    left: "right-full top-1/2 -translate-y-1/2 mr-2 w-[280px] max-w-[calc(100vw-2rem)] md:w-auto md:max-w-xs",
    right:
      "left-full top-1/2 -translate-y-1/2 ml-2 w-[280px] max-w-[calc(100vw-2rem)] md:w-auto md:max-w-xs",
  };

  const arrowClasses = {
    top: "top-full left-[40%] md:left-1/2 md:-translate-x-1/2 border-l-transparent border-r-transparent border-b-transparent border-t-gray-900",
    bottom:
      "bottom-full left-[40%] md:left-1/2 md:-translate-x-1/2 border-l-transparent border-r-transparent border-t-transparent border-b-gray-900",
    left: "left-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-r-transparent border-l-gray-900",
    right:
      "right-full top-1/2 -translate-y-1/2 border-t-transparent border-b-transparent border-l-transparent border-r-gray-900",
  };

  return (
    <div className="relative inline-block">
      <div
        onMouseEnter={showTooltip}
        onMouseLeave={hideTooltip}
        onFocus={() => {
          setIsFocused(true);
          showTooltip();
        }}
        onBlur={() => {
          setIsFocused(false);
          hideTooltip();
        }}
        aria-describedby={
          isVisible || isFocused ? tooltipId.current : undefined
        }
        tabIndex={0}
        className="cursor-help"
      >
        {children}
      </div>

      {(isVisible || isFocused) && (
        <div
          id={tooltipId.current}
          role="tooltip"
          className={`absolute z-50 px-3 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg shadow-lg whitespace-normal break-words ${positionClasses[position]} animate-fade-in-up`}
        >
          {content}
          {/* Arrow */}
          <div
            className={`absolute w-0 h-0 border-4 ${arrowClasses[position]}`}
            aria-hidden="true"
          />
        </div>
      )}
    </div>
  );
}
